import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import FormInput from '../../shared/components/ui/FormInput';
import Button from '../../shared/components/ui/Button';
import {
  getInventoryListWithCounts,
  setInventoryStatus,
  bulkSetInventoryStatus,
  createInventory,
  getMyVendors,
  createRFQ,
  updateInventory,
  downloadStockTemplate,
  uploadStockTemplate,
  getAllCategories,
  getInventoryByItem
} from '../services/api/implementation/impl';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import toast from 'react-hot-toast';
import Loader from '../../shared/components/ui/Loader';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import {
  Pencil,
  Trash,
  Plus,
  CirclePlus,
  Download,
  Upload,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  History,
  Warehouse
} from 'lucide-react';
import StockEntriesModal from '../components/inventory/StockEntriesModal';
import StockHistoryModal from '../components/inventory/StockHistoryModal';
import { formatINR, formatRateINR } from '../utils/inventoryValue';
import Modal from '../../shared/components/ui/Modal';
import Multiselect from 'multiselect-react-dropdown';
import RFQForm from '../components/forms/RFQForm';
import { createPortal } from 'react-dom';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { AddMaterials } from '../components/forms/AddMaterials';
import { useLocation } from 'react-router-dom';
import { resolveReturnTo, useSmartBack } from '../../shared/navigation';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { StatusPill } from '../../shared/components/ui/primitives';
import { criticalityBand, formatCriticality, isCriticalityNotSet } from '../utils/criticalityBands';
import {
  getSavedInventoryProjectId,
  getSavedWarehouseId,
  saveInventoryProjectId,
  saveWarehouseId,
  useProjectWarehouses,
  validWarehouseId
} from '../hooks/useInventorySelection';

// Stock quantities can be fractional (kg, m, litres); the server keeps 3 places.
const QTY_PATTERN = /^\d+(\.\d{0,3})?$/;
const isValidQty = (value) => QTY_PATTERN.test(String(value ?? '').trim());
const roundQty = (value) => Math.round((Number(value) || 0) * 1000) / 1000;

// Lent out / Borrowed: open lends only; a dash when there are none.
const LendCell = ({ value }) => {
  const qty = roundQty(value);
  return (
    <td className={`px-4 py-2 text-sm ${qty > 0 ? 'font-medium text-amber-700' : 'text-gray-400'}`}>
      {qty > 0 ? qty : '—'}
    </td>
  );
};

/** Small on/off switch, same look as the attendance toggle in Sales Reports. */
// One row action: a real button (keyboard and screen readers reach it) with
// the same 32px target and hover for every icon in the Actions column.
const RowIconButton = ({ label, onClick, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-[#2a565e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a565e]">
    {children}
  </button>
);

const RowSwitch = ({ checked, disabled, onChange, label }) => (
  <label
    className={`relative inline-flex items-center ${disabled ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
    title={label}>
    <input
      type="checkbox"
      role="switch"
      aria-checked={checked}
      aria-busy={disabled || undefined}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
      className="sr-only peer"
    />
    <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-[#2a565e] peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
  </label>
);

const Stock = () => {
  const location = useLocation();
  const hasReturnTo = Boolean(resolveReturnTo(location));
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const { collapsed } = useSidebar();
  const { projects, projectsForDropdown, isProjectsLoading, getProjectById } = useProjectsList();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [selectedItemsForRFQ, setSelectedItemsForRFQ] = useState([]);
  const [stockCriticality, setStockCriticality] = useState('');
  const [daysFilter, setDaysFilter] = useState('90'); // Default to 90 days
  const [statusFilter, setStatusFilter] = useState('active');
  // The server returns at most 500 rows per request, so a large project
  // (1,400+ items) used to show only its first 500 with no way to reach the rest.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [pendingStatusIds, setPendingStatusIds] = useState([]);
  // { [id]: boolean } for rows whose status was just changed: the switch,
  // pill and row shading show the new state as soon as it is clicked instead
  // of waiting for the list to come back from the server.
  const [optimisticStatus, setOptimisticStatus] = useState({});
  // { item, message } when a switch-off was blocked only by a stale requirement.
  const [forceConfirm, setForceConfirm] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const stockCriticalityHeaderRef = React.useRef(null);
  const daysFilterHeaderRef = React.useRef(null);
  // Only one of the two tables is on screen at a time, so they share this ref.
  const statusHeaderRef = React.useRef(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const [OpenAddMaterial, setOpenAddMaterial] = useState(false);
  const [stockCriticalityDropdownRect, setStockCriticalityDropdownRect] = useState(null);
  const [daysFilterDropdownRect, setDaysFilterDropdownRect] = useState(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm();
  const projectId = watch('project');
  const categoryId = watch('category');
  const queryClient = useQueryClient();

  // Warehouses: with one warehouse the page works as before (no choice shown).
  // With more, "All warehouses" shows project totals per item; picking one
  // shows that warehouse's own stock list.
  const {
    active: activeWarehouses,
    defaultWarehouse,
    hasMany: hasManyWarehouses,
    isLoading: isLoadingWarehouses
  } = useProjectWarehouses(projectId);
  const activeWarehouseKey = activeWarehouses.map((w) => w.id).join(',');
  const pickedWarehouse = watch('warehouse');
  const warehouseId = hasManyWarehouses ? validWarehouseId(pickedWarehouse, activeWarehouses) : '';
  const projectView = hasManyWarehouses && !warehouseId;
  const selectedWarehouse = warehouseId
    ? activeWarehouses.find((w) => Number(w.id) === Number(warehouseId))
    : defaultWarehouse;
  const [expandedItem, setExpandedItem] = useState(null);
  // W4.1: value cards above the table (hidden on servers without the report).

  useEffect(() => {
    if (projectId) {
      setValue('warehouse', validWarehouseId(getSavedWarehouseId(projectId), activeWarehouses));
    }
  }, [projectId, activeWarehouseKey, setValue]);

  // A new filter starts from page 1 with nothing selected. Done during render
  // (not in an effect) so no request goes out for the new filter at the old page.
  const filterKey = [
    projectId,
    categoryId,
    stockCriticality,
    daysFilter,
    statusFilter,
    pageSize,
    warehouseId,
    projectView
  ].join('|');
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setPage(1);
    setSelectedItems([]);
  }

  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory
  } = useQuery({
    queryKey: [
      'AllInventories',
      projectId,
      categoryId,
      stockCriticality,
      daysFilter,
      statusFilter,
      page,
      pageSize,
      warehouseId
    ],
    queryFn: () =>
      getInventoryListWithCounts(projectId, {
        categoryId,
        filter: stockCriticality,
        daysFilter,
        status: statusFilter,
        page,
        limit: pageSize,
        warehouseId
      }),
    // Keep the current page on screen while the next page loads, but never
    // show one project's rows while another project is loading.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[1] === projectId ? previous : undefined,
    // Wait for the warehouse list so a multi-warehouse project doesn't load the wrong view first.
    enabled: !!projectId && !projectView && !isLoadingWarehouses
  });

  // "All warehouses": one row per item with project totals, expandable per warehouse.
  const {
    data: byItemData,
    isLoading: isLoadingByItem,
    isFetching: isFetchingByItem
  } = useQuery({
    queryKey: ['inventoryByItem', projectId, categoryId, statusFilter, page, pageSize],
    queryFn: () =>
      getInventoryByItem(projectId, {
        categoryId,
        status: statusFilter,
        page,
        limit: pageSize
      }),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[1] === projectId ? previous : undefined,
    enabled: !!projectId && projectView
  });
  const byItemRows = byItemData?.items || [];

  // Status changes affect other screens' caches too (BOM link options,
  // pipeline low-stock lane), so refresh all of them, not just this list.
  const fetchInventory = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['AllInventories', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['inventoryByItem', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['projectWarehouses'] }),
      queryClient.invalidateQueries({ queryKey: ['boqBomInventoryOptions', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['inventory-pipeline-stock', projectId] })
    ]);
  };
  const AllInventories = inventoryData?.items;
  const inventoryCounts = inventoryData?.counts;
  // Status tab counts: items project-wide in "All warehouses", rows otherwise.
  const statusCounts = projectView ? byItemData?.counts || null : inventoryCounts;
  const listTotal = projectView ? byItemData?.total || 0 : inventoryData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(listTotal / pageSize));
  // "Create" clones the whole master list, so it must only show for a
  // project with no inventory at all, never for a filter that matched nothing.
  // Older servers send no counts; fall back to the previous rule there.
  const projectHasNoInventory = inventoryCounts
    ? inventoryCounts.total === 0
    : Boolean(AllInventories) && AllInventories.length < 1;

  // After rows leave the view (switched off, or the last page emptied), keep
  // the page in range and drop selected ids that are no longer on screen.
  useEffect(() => {
    if (projectView && byItemData && page > totalPages) {
      setPage(totalPages);
    }
  }, [projectView, byItemData, page, totalPages]);
  useEffect(() => {
    if (!inventoryData) return;
    if (page > totalPages) {
      setPage(totalPages);
    }
    const visible = new Set((inventoryData.items || []).map((i) => i.id));
    setSelectedItems((prev) => {
      const kept = prev.filter((id) => visible.has(id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [inventoryData, page, totalPages]);

  const {
    data: AllCategories,
    isLoading: isLoadingCategories,
    refetch: refetchCategories
  } = useQuery({
    queryKey: ['AllCategories'],
    queryFn: () => getAllCategories(1, 500)
  });
  useEffect(() => {
    refetchCategories();
  }, []);

  const {
    data: allVendors,
    isLoading: isLoadingVendors,
    refetch: refetchVendors
  } = useQuery({
    queryKey: ['allVendors'],
    queryFn: () => getMyVendors(1, 500)
  });

  const transformedDataCategories =
    AllCategories?.data?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id
    })) || [];

  useEffect(() => {
    if (projects) {
      // Project choice is shared with the other inventory screens.
      const savedProject = getSavedInventoryProjectId(projects);
      const savedCategory = localStorage.getItem('stock_selected_category');
      // The low-stock notification passes the project in the URL instead.
      const stateProjectId =
        location.state?.projectID ?? new URLSearchParams(location.search).get('projectID');

      // Priority: navigation state or link (e.g. Create Inventory task) → saved → first project
      let projectToSet = projects?.[0]?.id;
      if (
        stateProjectId != null &&
        projects?.find((p) => p.id === parseInt(stateProjectId, 10))
      ) {
        projectToSet = parseInt(stateProjectId, 10);
      } else if (savedProject) {
        projectToSet = savedProject;
      }

      setValue('project', projectToSet);

      // Set category if saved category exists
      if (savedCategory) {
        setValue('category', savedCategory);
      }
    }
  }, [projects, setValue, location.state?.projectID, location.search]);

  // The low-stock notification opens the list filtered to items below minimum.
  useEffect(() => {
    const linked = new URLSearchParams(location.search).get('criticality');
    if (['excellent', 'good', 'moderate', 'critical'].includes(linked)) {
      setStockCriticality(linked);
    }
  }, [location.search]);

  useEffect(() => {
    if (projectId) {
      fetchInventory();
      saveInventoryProjectId(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (categoryId) {
      // Save selected category to localStorage
      localStorage.setItem('stock_selected_category', categoryId.toString());
    } else if (categoryId === '') {
      // Clear category from localStorage when "All Categories" is selected
      localStorage.removeItem('stock_selected_category');
    }
  }, [categoryId]);

  const formatDate = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }); // May, Jun, etc.
    const year = date.getFullYear();

    // Add "st", "nd", "rd", "th"
    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return 'th'; // 11th, 12th, 13th
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  // Handle individual item selection
  const handleItemSelect = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedItems.length === AllInventories?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(AllInventories?.map((item) => item.id) || []);
    }
  };

  // Check if all items are selected
  const isAllSelected =
    AllInventories?.length > 0 && selectedItems.length === AllInventories?.length;

  // Get selected items count for display
  const selectedItemsCount = selectedItems.length;

  // Get unique category IDs from selected items
  const selectedItemCategoryIds = [
    ...new Set(selectedItemsForRFQ.map((item) => item.parent_category_id))
  ];

  // Filter vendors based on selected items' categories
  const filteredVendors =
    allVendors?.data?.filter((vendor) => {
      // Check if vendor has category mappings
      if (!vendor?.vendorCategoryMapping || vendor?.vendorCategoryMapping?.length === 0) {
        return false;
      }

      // Check if vendor has any category that matches the selected items' categories
      const vendorCategoryIds = vendor?.vendorCategoryMapping?.map(
        (mapping) => mapping.category_id
      );
      return selectedItemCategoryIds.some((categoryId) => vendorCategoryIds.includes(categoryId));
    }) || [];

  // Transform filtered vendors for dropdown
  const transformedVendors = filteredVendors?.map((vendor) => {
    // Get matching categories for this vendor
    const matchingCategories = vendor?.vendorCategoryMapping
      .filter((mapping) => selectedItemCategoryIds.includes(mapping.category_id))
      .map((mapping) => mapping.categroy?.name)
      .filter(Boolean);

    const categoryText = matchingCategories.length > 0 ? ` (${matchingCategories.join(', ')})` : '';

    return {
      name: `${vendor?.user?.name || ''}${categoryText}`,
      id: vendor?.id
    };
  });

  // Handle RFQ modal open
  const handleRFQClick = (item, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!item || !item.id) {
      console.error('Invalid item for RFQ');
      return;
    }

    // If there are selected items, use those; otherwise use the clicked item.
    // Switched-off items are left out: the site doesn't stock them.
    const itemsToShow = (
      selectedItems.length > 0
        ? AllInventories.filter((task) => selectedItems.includes(task.id))
        : [item]
    ).filter((task) => task.is_active !== false);
    if (!itemsToShow.length) {
      toast.error('Switch an item on before raising an RFQ for it.');
      return;
    }
    if (selectedItems.length > itemsToShow.length) {
      toast(`${selectedItems.length - itemsToShow.length} inactive item(s) left out of the RFQ.`);
    }

    setSelectedItemsForRFQ(itemsToShow);

    // Initialize quantities for all items with stock_to_be_ordered if not negative
    const initialQuantities = {};
    itemsToShow.forEach((task) => {
      const stockToBeOrdered = roundQty(task.stock_to_be_ordered);
      // Pre-populate with stock_to_be_ordered only if it's not negative
      const initialQuantity = stockToBeOrdered > 0 ? stockToBeOrdered : '';
      initialQuantities[task.id] = itemQuantities[task.id] || initialQuantity;
    });
    setItemQuantities(initialQuantities);

    setIsRFQModalOpen(true);
  };

  // Handle vendor selection
  const handleVendorSelect = (selectedList) => {
    const vendorIds = selectedList.map((vendor) => vendor.id);
    setSelectedVendors(vendorIds);
  };

  // Handle quantity change
  const handleQuantityChange = (itemId, quantity) => {
    setItemQuantities((prev) => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  // Handle RFQ submit
  const handleRFQSubmit = async (formPayload) => {
    // Validate vendor selection
    if (selectedVendors.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }

    // Validate quantities
    const invalidItems = selectedItemsForRFQ.filter((item) => {
      const quantity = itemQuantities[item.id];
      return !quantity || quantity <= 0;
    });

    if (invalidItems.length > 0) {
      toast.error('Please enter a quantity greater than 0 for all items');
      return;
    }

    const payload = {
      project_id: parseInt(projectId),
      ...formPayload
    };

    console.log('RFQ Payload:', payload);
    // Add your API call here
    try {
      const response = await createRFQ(payload);
      toast.success('RFQ submitted successfully');
      setIsRFQModalOpen(false);
      setSelectedVendors([]);
      setItemQuantities({});
      setSelectedItemsForRFQ([]);
    } catch (error) {
      console.error('Error creating RFQ:', error);
      toast.error(error.response?.data?.message || error?.message || 'Error creating RFQ');
    }
  };

  // Handle RFQ cancel
  const handleRFQCancel = () => {
    setIsRFQModalOpen(false);
    setSelectedVendors([]);
    setItemQuantities({});
    setSelectedItemsForRFQ([]);
  };

  // Check if submit button should be disabled
  const isSubmitDisabled = () => {
    // Check if vendors are selected
    if (selectedVendors.length === 0) return true;

    // Check if all items have valid quantities
    const hasInvalidQuantities = selectedItemsForRFQ.some((item) => {
      const quantity = itemQuantities[item.id];
      return !quantity || quantity <= 0;
    });

    return hasInvalidQuantities;
  };

  const { mutate: createInventoryfn } = useMutation({
    mutationFn: (payload) => createInventory(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Stock created successfully');
      await fetchInventory();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new stock', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating stock');
    }
  });

  const markPending = (ids, on) =>
    setPendingStatusIds((prev) =>
      on ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))
    );
  const rowIsActive = (item) =>
    Object.prototype.hasOwnProperty.call(optimisticStatus, item.id)
      ? optimisticStatus[item.id]
      : item.is_active !== false;
  const clearOptimistic = (ids) =>
    setOptimisticStatus((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });

  // Switch one row on or off. A switch-off blocked only by a stale requirement
  // offers "Switch off anyway"; any other block just shows the reason.
  const handleToggleStatus = async (item, force = false) => {
    const turnOn = item.is_active === false;
    markPending([item.id], true);
    // Flip the row at once; a failed call (or a block that needs confirming)
    // puts it back below.
    setOptimisticStatus((prev) => ({ ...prev, [item.id]: turnOn }));
    try {
      await setInventoryStatus(item.id, turnOn, force);
      toast.success(`${item.item_name} switched ${turnOn ? 'on' : 'off'}`);
      setForceConfirm(null);
      await fetchInventory();
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Could not change the item status';
      if (!turnOn && !force && err?.response?.data?.data?.can_force) {
        setForceConfirm({ item, message });
      } else {
        toast.error(message);
      }
    } finally {
      // Cleared only after the refetch above has landed, so the row never
      // snaps back to the old state in between.
      clearOptimistic([item.id]);
      markPending([item.id], false);
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedItems.length || !projectId) return;
    // Only rows whose status actually changes, so the count is honest.
    const ids = (AllInventories || [])
      .filter((i) => selectedItems.includes(i.id) && (i.is_active !== false) !== isActive)
      .map((i) => i.id);
    if (!ids.length) {
      toast(`The selected items are already ${isActive ? 'active' : 'inactive'}.`);
      return;
    }
    markPending(ids, true);
    setIsSubmitting(true);
    try {
      const result = await bulkSetInventoryStatus(Number(projectId), ids, isActive);
      const blocked = result?.blocked || [];
      if (result?.updated) {
        toast.success(`${result.updated} item(s) switched ${isActive ? 'on' : 'off'}`);
      }
      if (blocked.length) {
        const shown = blocked
          .slice(0, 3)
          .map((b) => `${b.itemName || `#${b.itemId}`}: ${(b.reasons || []).join(', ')}`)
          .join('\n');
        const more = blocked.length > 3 ? `\nand ${blocked.length - 3} more` : '';
        toast.error(`${blocked.length} item(s) can't be switched off.\n${shown}${more}`, {
          duration: 8000,
          style: { whiteSpace: 'pre-line' }
        });
      }
      setSelectedItems([]);
      await fetchInventory();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not change the status');
    } finally {
      markPending(ids, false);
      setIsSubmitting(false);
    }
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [stockEntriesItem, setStockEntriesItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null); // a stock row (one item in one warehouse)
  // Tracked outside react-hook-form: FormInput's own onChange hides edits from watch().
  const [editPhysicalStock, setEditPhysicalStock] = useState('');
  const [editReason, setEditReason] = useState('');
  // Excel upload asks for a reason first; null = closed.
  const [uploadReason, setUploadReason] = useState(null);
  const {
    register: editRegister,
    handleSubmit: handleEditSubmit,
    setValue: setEditValue,
    reset: resetEditForm,
    formState: { errors: editErrors }
  } = useForm();

  // Open edit modal and prefill values
  const handleEditClick = (item) => {
    setEditItem(item);
    setIsEditModalOpen(true);
    // Prefill all fields
    setEditValue('avg_acquiring_days', item.avg_acquiring_days || 0);
    setEditValue('safetyNet', item.safetyNet || 0);

    setEditValue('minimumQuantity', item.minimumQuantity || 0);
    setEditValue('physicalStock', item.physicalStock || 0);
    setEditValue('reason', '');
    setEditReason('');
    setEditPhysicalStock(String(item.physicalStock || 0));
  };

  // A Physical Stock change is posted as a stock movement, so it needs a reason.
  const physicalStockChanged =
    !!editItem &&
    String(editPhysicalStock).trim() !== '' &&
    Number(editPhysicalStock) !== Number(editItem.physicalStock || 0);

  // Handle edit modal close
  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditItem(null);
    resetEditForm();
  };

  // Edit form submit
  const onEditSubmit = async (data) => {
    if (!editItem) return;
    if (data.safetyNet > 100) {
      toast.error('Safety Net cannot be greater than 100%');
      return;
    }
    if (!isValidQty(data.minimumQuantity) || !isValidQty(data.physicalStock)) {
      toast.error('Min Qty and Physical Stock must be 0 or more, with up to 3 decimals');
      return;
    }
    const reason = editReason.trim();
    if (Number(data.physicalStock) !== Number(editItem.physicalStock || 0) && !reason) {
      toast.error('Please enter a reason for changing Physical Stock');
      return;
    }
    setIsSubmitting(true);
    try {
      // All fields are required and must be numbers >= 0
      const payload = {
        avg_acquiring_days: String(data.avg_acquiring_days),
        safetyNet: String(data.safetyNet),
        minimumQuantity: String(data.minimumQuantity),
        physicalStock: String(data.physicalStock),
        ...(reason && { reason })
      };
      await updateInventory(editItem.id, payload);
      toast.success('Inventory updated successfully');
      handleEditModalClose();
      await fetchInventory();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Error updating inventory');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (projectId) {
      try {
        const response = await downloadStockTemplate(projectId); // should return a
        console.log('response', response);
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        // Find the project name
        const project = projects?.find((p) => p.id === projectId || p.value === projectId);
        const projectName = project?.name || project?.label || 'project';
        // Sanitize project name for filename
        const safeProjectName = projectName?.replace(/[^a-zA-Z0-9-_]/g, '_');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeProjectName}_Inventory.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || 'Failed to download template'
        );
      }
    } else {
      toast.error('Please select a project');
    }
  };

  const handleUploadTemplate = async (reason) => {
    if (projectId) {
      // Create a file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.xlsx,.xls';
      fileInput.style.display = 'none';

      // Add event listener for file selection
      fileInput.onchange = async (event) => {
        const file = event.target.files[0];
        if (file) {
          try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('file', file);
            if (reason?.trim()) formData.append('reason', reason.trim());

            const response = await uploadStockTemplate(projectId, formData);
            console.log('Uploaded template', response);
            toast.success('Template uploaded successfully');
            await fetchInventory();
          } catch (error) {
            console.error('Upload error:', error);
            toast.error(
              error?.response?.data?.message || error?.message || 'Failed to upload template'
            );
          } finally {
            setIsSubmitting(false);
          }
        }

        // Clean up the file input
        document.body.removeChild(fileInput);
      };

      // Add to DOM and trigger click
      document.body.appendChild(fileInput);
      fileInput.click();
    } else {
      toast.error('Please select a project');
    }
  };

  // PortalDropdown component
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    if (type === 'criticality' && stockCriticalityHeaderRef?.current) {
      setStockCriticalityDropdownRect(stockCriticalityHeaderRef?.current?.getBoundingClientRect());
    }

    if (type === 'days' && daysFilterHeaderRef?.current) {
      setDaysFilterDropdownRect(daysFilterHeaderRef?.current?.getBoundingClientRect());
    }

    if (type === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef.current.getBoundingClientRect());
    }

    setOpenDropdown(type);
  };

  // Handler to close dropdown
  const handleCloseDropdown = () => setOpenDropdown(null);

  // Handler to select item (can be extended to filter, etc.)
  const handleSelectDropdownItem = (type, value) => {
    setOpenDropdown(null);

    if (type === 'criticality') {
      setStockCriticality(value);
    }

    if (type === 'days') {
      setDaysFilter(value);
    }

    if (type === 'status') {
      setStatusFilter(value);
    }
  };

  // Status filter, in the Status column header of both tables. The header
  // names the current choice, so a filtered list is never mistaken for all of it.
  const STATUS_OPTIONS = [
    { value: 'active', label: 'Active', count: statusCounts?.active },
    { value: 'inactive', label: 'Inactive', count: statusCounts?.inactive },
    { value: 'all', label: 'All', count: statusCounts?.total }
  ];
  const renderStatusHeader = () => {
    const current = STATUS_OPTIONS.find((o) => o.value === statusFilter) || STATUS_OPTIONS[0];
    return (
      <th className="px-3 py-2 border-b font-normal whitespace-nowrap" ref={statusHeaderRef}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={openDropdown === 'status'}
          className="inline-flex items-center gap-1 align-middle"
          onClick={(e) => {
            e.stopPropagation();
            handleHeaderDropdownClick('status', e);
          }}>
          <span>Status</span>
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
              current.value === 'active' ? 'bg-white/70 text-gray-600' : 'bg-[#2a565e] text-white'
            }`}>
            {current.label}
          </span>
          <ChevronDown size={16} />
        </button>
        {openDropdown === 'status' && statusDropdownRect && (
          <PortalDropdown anchorRect={statusDropdownRect} onClose={handleCloseDropdown}>
            {STATUS_OPTIONS.map((option) => (
              <div
                key={option.value}
                role="option"
                aria-selected={option.value === statusFilter}
                className={`flex justify-between gap-6 px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                  option.value === statusFilter ? 'bg-blue-100' : ''
                }`}
                onClick={() => handleSelectDropdownItem('status', option.value)}>
                <span>{option.label}</span>
                {option.count != null && <span className="text-gray-500">{option.count}</span>}
              </div>
            ))}
          </PortalDropdown>
        )}
      </th>
    );
  };

  // Add a global click handler to close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdown) return;
    const handleClick = () => setOpenDropdown(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [openDropdown]);

  return (
    <Layout>
      <Loader
        isVisible={
          isSubmitting ||
          isProjectsLoading ||
          isLoadingInventory ||
          isLoadingVendors ||
          (projectView && isLoadingByItem)
        }
      />
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={hasReturnTo}
          onBack={hasReturnTo ? onBack : undefined}
          data={{ name: 'Stock' }}
        />
        <div
          className={`flex justify-between items-center gap-2 fixed  z-30  bg-white px-6 py-3 pb-6  transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <div className="flex justify-between items-end w-[100%] gap-3">
            <div className="flex flex-1 justify-start items-end gap-3 flex-nowrap min-w-0">
              <FormInput
                width={hasManyWarehouses ? 24 : 30}
                label={'Project'}
                register={register}
                register_key={'project'}
                isRequired={true}
                type={'dropdown'}
                options={[
                  { label: 'Select Project', value: '', disabled: true, selected: true }, // Add the "Select Project" option first
                  ...projectsForDropdown
                ]}
                validation={{ required: 'Project is required' }}
                errors={errors}
              />
              {/* Always say which warehouse this is. One warehouse: its name as
                  plain text, since there is nothing to pick. More: pick one, or
                  All warehouses. */}
              {hasManyWarehouses ? (
                <FormInput
                  width={20}
                  label={'Warehouse'}
                  register={register}
                  register_key={'warehouse'}
                  isRequired={false}
                  type={'dropdown'}
                  options={[
                    { label: 'All warehouses', value: '' },
                    ...activeWarehouses.map((w) => ({
                      label: `${w.name}${w.is_default ? ' (default)' : ''}`,
                      id: w.id,
                      value: w.id
                    }))
                  ]}
                  onDropdownChange={(value) => saveWarehouseId(projectId, value)}
                  errors={errors}
                />
              ) : (
                <div className="flex min-w-0 max-w-[16rem] flex-shrink flex-col gap-2 text-gray-700">
                  <span className="text-gray-600">Warehouse</span>
                  <div
                    className="flex h-[42px] min-w-0 items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 text-gray-700"
                    title={defaultWarehouse?.name || ''}>
                    <Warehouse size={16} className="flex-shrink-0 text-[#2a565e]" />
                    <span className="truncate">
                      {defaultWarehouse?.name || (isLoadingWarehouses ? 'Loading…' : '—')}
                    </span>
                  </div>
                </div>
              )}
              <FormInput
                width={18}
                label={'Category'}
                register={register}
                register_key={'category'}
                isRequired={false}
                type={'dropdown'}
                options={[
                  { label: 'All Categories', value: '', selected: true }, // Add the "Select Project" option first
                  ...transformedDataCategories
                ]}
                validation={{ required: 'Category is required' }}
                errors={errors}
              />
            </div>

            <div className="flex items-center gap-3">
              {projectHasNoInventory ? (
                // The warehouse is named in the filter beside it; the button
                // stays short and says it in its tooltip.
                <button
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-[#2a565e] bg-[#2a565e] px-4 py-2 font-semibold text-white disabled:opacity-60"
                  disabled={isSubmitting || isLoadingInventory}
                  type="button"
                  title={
                    selectedWarehouse
                      ? `Create the stock list for ${selectedWarehouse.name} from the master item list`
                      : undefined
                  }
                  onClick={() => {
                    if (projectId && !isSubmitting && projectId !== '' && projectId !== null) {
                      createInventoryfn({
                        project_id: projectId,
                        ...(selectedWarehouse ? { warehouse_id: selectedWarehouse.id } : {})
                      });
                    }
                  }}>
                  <Plus size={16} />
                  <span>Create stock list</span>
                </button>
              ) : selectedItems.length > 0 ? (
                <div className="flex flex-shrink-0 items-center gap-2">
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    {selectedItems.length} selected
                  </span>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleBulkStatus(true)}
                    className="flex items-center gap-2 text-[#2a565e] px-3 py-2 text-sm rounded-md border border-[#2a565e] font-semibold whitespace-nowrap disabled:opacity-60">
                    Activate
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleBulkStatus(false)}
                    className="flex items-center gap-2 text-gray-700 px-3 py-2 text-sm rounded-md border border-gray-400 font-semibold whitespace-nowrap disabled:opacity-60">
                    Deactivate
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      const first = AllInventories?.find((i) => selectedItems.includes(i.id));
                      if (first) handleRFQClick(first, e);
                    }}
                    className="flex items-center gap-2 bg-[#2a565e] text-white px-3 py-2 text-sm rounded-md border border-[#2a565e] font-semibold whitespace-nowrap disabled:opacity-60">
                    RFQ
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedItems([])}
                    className="px-2 py-2 text-sm text-gray-500 hover:text-gray-800 whitespace-nowrap">
                    Clear
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    className="flex items-center gap-2  text-[#2a565e] px-4 py-2 rounded-md border border-[#2a565e] whitespace-nowrap"
                    disabled={isSubmitting}
                    type="button"
                    onClick={() => {
                      setOpenAddMaterial(true);
                    }}>
                    <Plus size={16} />
                    <span className="font-semibold">Add Item</span>
                  </button>
                  <button
                    className="flex items-center gap-2 bg-[#2a565e] text-white px-4 py-2 rounded-md border border-[#2a565e]"
                    disabled={isSubmitting}
                    type="button"
                    onClick={() => {
                      handleDownloadTemplate();
                    }}>
                    <Download size={16} />
                    <span className="font-semibold">Template</span>
                  </button>

                  <button
                    className="flex items-center gap-2 bg-[#2a565e] text-white px-4 py-2 rounded-md border border-[#2a565e]"
                    disabled={isSubmitting}
                    type="button"
                    onClick={() => {
                      if (!projectId) {
                        toast.error('Please select a project');
                        return;
                      }
                      setUploadReason('');
                    }}>
                    <Upload size={16} />
                    <span>{isSubmitting ? 'Uploading...' : 'Upload'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GridWrapper width={100}>
        <div className="shadow rounded-lg overflow-auto relative mt-[12rem] h-[calc(100vh-17.5rem)]">
          {projectView ? (
            <table className="min-w-full rounded-t-xl text-left">
              <thead className="bg-gray-200 !text-gray-600 sticky top-0 z-20">
                <tr className="font-normal h-[3rem]">
                  <th className="px-3 py-2 border-b font-normal w-8" />
                  <th className="px-3 py-2 border-b font-normal">Name</th>
                  {renderStatusHeader()}
                  <th className="px-3 py-2 border-b font-normal">Category</th>
                  <th className="px-3 py-2 border-b font-normal">Unit</th>
                  <th className="px-3 py-2 border-b font-normal">Min Qty</th>
                  <th className="px-3 py-2 border-b font-normal">Physical Stock</th>
                  <th className="px-3 py-2 border-b font-normal">In Transit</th>
                  <th className="px-3 py-2 border-b font-normal">Lent out</th>
                  <th className="px-3 py-2 border-b font-normal">Borrowed</th>
                  <th className="px-3 py-2 border-b font-normal">Available Stock</th>
                  <th className="px-3 py-2 border-b font-normal">Stock value</th>
                  <th className="px-3 py-2 border-b font-normal">Required Stock</th>
                  <th className="px-3 py-2 border-b font-normal">Consumption</th>
                  <th className="px-3 py-2 border-b font-normal">Stock Criticality (%)</th>
                  <th className="px-3 py-2 border-b font-normal">Stock to be ordered</th>
                  <th className="px-3 py-2 border-b font-normal">Warehouses</th>
                </tr>
              </thead>
              <tbody>
                {byItemRows.length > 0 ? (
                  byItemRows.map((row) => {
                    const open = expandedItem === row.item_key;
                    const withStock = (row.warehouses || []).filter(
                      (w) => Number(w.physicalStock) > 0 || Number(w.inTransitStock) > 0
                    ).length;
                    return (
                      <React.Fragment key={row.item_key}>
                        <tr
                          className={`border-b bg-white h-[3.5rem] cursor-pointer hover:bg-gray-50 ${
                            row.is_active === false ? 'opacity-[0.55]' : ''
                          }`}
                          onClick={() => setExpandedItem(open ? null : row.item_key)}>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </td>
                          <td className="px-4 py-2 text-sm">{row.item_name || '—'}</td>
                          <td className="px-4 py-2 text-sm">
                            <StatusPill dense tone={row.is_active === false ? 'neutral' : 'success'}>
                              {row.is_active === false ? 'Inactive' : 'Active'}
                            </StatusPill>
                          </td>
                          <td className="px-4 py-2 text-sm">{row.category_name || '—'}</td>
                          <td className="px-4 py-2 text-sm">{row.unit || '—'}</td>
                          <td className="px-4 py-2 text-sm">{roundQty(row.minimumQuantity)}</td>
                          <td className="px-4 py-2 text-sm">{roundQty(row.physicalStock)}</td>
                          <td className="px-4 py-2 text-sm">{roundQty(row.inTransitStock)}</td>
                          <LendCell value={row.lent_out} />
                          <LendCell value={row.borrowed} />
                          <td className="px-4 py-2 text-sm">{roundQty(row.availableStock)}</td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            {formatINR(row.stock_value)}
                            {Number(row.physicalStock) > 0 &&
                              (row.warehouses || []).some(
                                (w) => Number(w.physicalStock) > 0 && w.avg_rate == null
                              ) && (
                                <span
                                  className="block text-[11px] text-gray-400"
                                  title="Some of this stock has no rate yet, so it counts as ₹0">
                                  part without a rate
                                </span>
                              )}
                          </td>
                          <td className="px-4 py-2 text-sm">{roundQty(row.requiredStock)}</td>
                          <td className="px-4 py-2 text-sm">{roundQty(row.immediateConsumption)}</td>
                          <td
                            className={`px-4 py-2 text-sm ${criticalityBand(row.stock_criticality).cell}`}
                            title={
                              isCriticalityNotSet(row.stock_criticality)
                                ? 'Min Qty is 0, so there is no target to measure against'
                                : undefined
                            }>
                            {formatCriticality(row.stock_criticality)}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {Math.max(0, roundQty(row.stock_to_be_ordered))}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {withStock} of {(row.warehouses || []).length} with stock
                          </td>
                        </tr>
                        {open &&
                          (row.warehouses || []).map((w) => (
                            <tr key={`${row.item_key}-${w.inventory_id}`} className="border-b bg-gray-50">
                              <td />
                              <td className="px-4 py-2 text-sm" colSpan={5}>
                                <span className="text-gray-800">{w.warehouse_name || 'No warehouse'}</span>
                                {w.is_default && (
                                  <span className="ml-2 rounded-full bg-[#E9F5F6] px-2 py-0.5 text-[11px] font-semibold text-[#2A565E]">
                                    Default
                                  </span>
                                )}
                                {w.warehouse_active === false && (
                                  <span className="ml-2 text-xs text-gray-400">Inactive</span>
                                )}
                                {w.is_active === false && (
                                  <span className="ml-2 text-xs text-gray-400">(item off)</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm">{roundQty(w.physicalStock)}</td>
                              <td className="px-4 py-2 text-sm">{roundQty(w.inTransitStock)}</td>
                              <LendCell value={w.lent_out} />
                              <LendCell value={w.borrowed} />
                              <td className="px-4 py-2 text-sm">{roundQty(w.availableStock)}</td>
                              <td className="px-4 py-2 text-sm whitespace-nowrap">
                                {w.stock_value != null
                                  ? formatINR(w.stock_value)
                                  : w.avg_rate == null
                                    ? '—'
                                    : formatINR(Number(w.physicalStock || 0) * Number(w.avg_rate))}
                                <span className="block text-[11px] text-gray-500">
                                  {w.avg_rate == null
                                    ? Number(w.physicalStock) > 0
                                      ? 'No rate yet'
                                      : 'Avg rate —'
                                    : `Avg rate ${formatRateINR(w.avg_rate)}${
                                        row.unit ? ` / ${row.unit}` : ''
                                      }`}
                                </span>
                              </td>
                              <td colSpan={4} />
                              <td className="px-4 py-2 text-sm whitespace-nowrap">
                                {w.inventory_id && (
                                  <button
                                    type="button"
                                    className="mr-3 text-xs font-semibold text-[#2A565E] hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHistoryItem({
                                        ...w,
                                        item_name: row.item_name,
                                        unit: row.unit
                                      });
                                    }}>
                                    History
                                  </button>
                                )}
                                {w.warehouse_id && w.warehouse_active !== false && (
                                  <button
                                    type="button"
                                    className="text-xs font-semibold text-[#2A565E] hover:underline"
                                    onClick={() => {
                                      setValue('warehouse', w.warehouse_id);
                                      saveWarehouseId(projectId, w.warehouse_id);
                                    }}>
                                    Open this warehouse
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={17} className="text-center py-7">
                      {isFetchingByItem ? (
                        <span className="text-sm text-gray-400">Loading…</span>
                      ) : (
                        <div className="flex flex-col items-center justify-center">
                          <img
                            src={noDataIcon}
                            alt="No Data"
                            className="w-[25rem] h-[20rem] object-cover"
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
          <table className="min-w-full rounded-t-xl text-left">
            <thead className="bg-gray-200 !text-gray-600 sticky top-0 z-20">
              <tr className="font-normal h-[3rem]">
                <th className="px-3 py-2 border-b font-normal">
                  <div className="flex items-start gap-2 flex-col">
                    {/* <span className="text-sm text-gray-600">Select All</span> */}
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                    />
                  </div>
                </th>
                <th className="px-3 py-2 border-b font-normal">SKU</th>
                <th className="px-3 py-2 border-b font-normal">Name</th>
                {renderStatusHeader()}
                <th className="px-3 py-2 border-b font-normal">HSN</th>
                <th className="px-3 py-2 border-b font-normal">Unit</th>
                <th className="px-3 py-2 border-b font-normal">Avg days to acquire</th>
                <th className="px-3 py-2 border-b font-normal">Safety net (%)</th>
                <th className="px-3 py-2 border-b font-normal">Safety days</th>
                <th className="px-3 py-2 border-b font-normal">Min Qty</th>
                <th className="px-3 py-2 border-b font-normal">Physical Stock</th>
                <th className="px-3 py-2 border-b font-normal">Avg rate</th>
                <th className="px-3 py-2 border-b font-normal">Stock value</th>
                <th className="px-3 py-2 border-b font-normal">In Transit</th>
                <th className="px-3 py-2 border-b font-normal">Lent out</th>
                <th className="px-3 py-2 border-b font-normal">Borrowed</th>
                <th className="px-3 py-2 border-b font-normal">Available Stock</th>
                <th className="px-3 py-2 border-b font-normal" ref={daysFilterHeaderRef}>
                  <span>Required Stock (in {daysFilter} days)</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('days', e);
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'days' && daysFilterDropdownRect && (
                    <PortalDropdown
                      anchorRect={daysFilterDropdownRect}
                      onClose={handleCloseDropdown}>
                      {[
                        { id: '90', value: '90', label: '90 Days' },
                        { id: '60', value: '60', label: '60 Days' },
                        { id: '30', value: '30', label: '30 Days' },
                        { id: '10', value: '10', label: '10 Days' },
                        { id: '7', value: '7', label: '7 Days' }
                      ].map((option, idx) => (
                        <div
                          key={option.id || idx}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            option.id === daysFilter && 'bg-blue-100'
                          }`}
                          onClick={() => handleSelectDropdownItem('days', option.id)}>
                          {option.label}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-3 py-2 border-b font-normal">Consumption</th>
                <th className="px-3 py-2 border-b font-normal" ref={stockCriticalityHeaderRef}>
                  <span>Stock Criticality (%)</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('criticality', e);
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'criticality' && stockCriticalityDropdownRect && (
                    <PortalDropdown
                      anchorRect={stockCriticalityDropdownRect}
                      onClose={handleCloseDropdown}>
                      {[
                        {
                          id: '',
                          value: '',
                          label: stockCriticality ? 'Clear Filter' : 'Select Criticality',
                          selected: true
                        },
                        {
                          id: 'excellent',
                          value: 'excellent',
                          label: 'Excellent (≥150%)',
                          selected: true
                        },
                        {
                          id: 'good',
                          value: 'good',
                          label: 'Good (125–149%)',
                          selected: true
                        },
                        {
                          id: 'moderate',
                          value: 'moderate',
                          label: 'Moderate (100–124%)',
                          selected: true
                        },
                        {
                          id: 'critical',
                          value: 'critical',
                          label: 'Critical (<100%)',
                          selected: true
                        }
                      ].map((option, idx) => (
                        <div
                          key={option.id || idx}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            option.id === '' && 'bg-gray-100'
                          } ${option.id === stockCriticality && 'bg-blue-100'}`}
                          onClick={() => handleSelectDropdownItem('criticality', option.id)}>
                          {option.label}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-3 py-2 border-b font-normal">Stock to be ordered</th>
                {/* Pinned to the right edge, so the actions stay in view on this wide table. */}
                <th className="sticky right-0 z-10 px-3 py-2 border-b font-normal bg-gray-200 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {AllInventories && AllInventories?.length > 0 ? (
                AllInventories?.map((task) => {
                  const active = rowIsActive(task);
                  return (
                    <tr
                      key={task.id}
                      className={`border-b bg-white h-[3.5rem] ${!active ? 'opacity-[0.55]' : ''}`}>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(task.id)}
                          onChange={() => handleItemSelect(task.id)}
                          className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">{task.id || '0'}</td>
                      <td className="px-4 py-2 text-sm cursor-pointer">{task.item_name || '0'}</td>
                      <td className="px-4 py-2 text-sm">
                        <StatusPill dense tone={!active ? 'neutral' : 'success'}>
                          {!active ? 'Inactive' : 'Active'}
                        </StatusPill>
                      </td>
                      <td className="px-4 py-2 text-sm">{task.hsn_code || task.item?.hsn_code || '-'}</td>
                      <td className="px-4 py-2 text-sm">{task.unit || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.avg_acquiring_days || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.safetyNet || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.safetyDays || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.minimumQuantity || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.physicalStock || '0'}</td>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {task.avg_rate == null ? '—' : `₹${Number(task.avg_rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                      </td>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {task.avg_rate == null
                          ? '—'
                          : `₹${Math.round(Number(task.physicalStock || 0) * Number(task.avg_rate)).toLocaleString('en-IN')}`}
                      </td>
                      <td className="px-4 py-2 text-sm">{task.inTransitStock || '0'}</td>
                      <LendCell value={task.lent_out} />
                      <LendCell value={task.borrowed} />
                      <td className="px-4 py-2 text-sm">{task.availableStock || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.requiredStock || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.immediateConsumption || '0'}</td>
                      <td
                        className={`px-4 py-2 text-sm ${criticalityBand(task.stock_criticality).cell}`}
                        title={
                          isCriticalityNotSet(task.stock_criticality)
                            ? 'Min Qty is 0, so there is no target to measure against'
                            : undefined
                        }>
                        {formatCriticality(task.stock_criticality)}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {Math.max(0, roundQty(task.stock_to_be_ordered))}
                      </td>
                      <td className="sticky right-0 bg-white px-3 py-2 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)]">
                        <div className="flex items-center gap-1">
                          <RowIconButton label="Edit stock and planning" onClick={() => handleEditClick(task)}>
                            <Pencil size={16} />
                          </RowIconButton>
                          <RowIconButton
                            label="Stock & rate: old stock with purchase price"
                            onClick={() => setStockEntriesItem(task)}>
                            <IndianRupee size={16} />
                          </RowIconButton>
                          <RowIconButton label="Stock history" onClick={() => setHistoryItem(task)}>
                            <History size={16} />
                          </RowIconButton>
                          {/* A disabled button gets no hover, so the reason sits on the wrapper. */}
                          <span
                            className="ml-1"
                            title={!active ? 'Switch on to raise an RFQ' : 'Request for quotation'}>
                            <button
                              type="button"
                              disabled={!active}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRFQClick(task, e);
                              }}
                              className="h-7 rounded-md border border-[#2a565e] px-2.5 text-xs font-semibold text-[#2a565e] hover:bg-[#2a565e] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent">
                              RFQ
                            </button>
                          </span>
                          <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
                          <RowSwitch
                            checked={active}
                            disabled={pendingStatusIds.includes(task.id)}
                            label={!active ? 'Switch on' : 'Switch off'}
                            onChange={() => handleToggleStatus(task)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={22} className="text-center py-7">
                    <div className="flex flex-col items-center justify-center">
                      <img
                        src={noDataIcon}
                        alt="No Data"
                        className="w-[25rem] h-[20rem] object-cover"
                      />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
        {listTotal > 0 && (
          <div className="mt-2">
            <PaginationComponent
              rangeLabel={(() => {
                const shown = projectView ? byItemRows.length : AllInventories?.length || 0;
                return `${
                  shown ? `${(page - 1) * pageSize + 1}–${(page - 1) * pageSize + shown}` : '0'
                } of ${listTotal}`;
              })()}
              page={page}
              totalPages={totalPages}
              onPageChange={(p) => {
                setSelectedItems([]);
                setPage(Math.min(Math.max(1, p), totalPages));
              }}
              isFetching={projectView ? isFetchingByItem : isFetchingInventory}
              pageSize={pageSize}
              pageSizeOptions={[50, 100, 250, 500]}
              onPageSizeChange={(n) => setPageSize(Number(n))}
            />
          </div>
        )}
      </GridWrapper>

      {/* RFQ Modal */}
      {isRFQModalOpen && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          isOpen={isRFQModalOpen}
          width={90}
          name="Request for Quotation (RFQ)"
          onClose={() => setIsRFQModalOpen(false)}>
          <RFQForm
            transformedVendors={transformedVendors}
            selectedVendors={selectedVendors}
            handleVendorSelect={handleVendorSelect}
            selectedItemsForRFQ={selectedItemsForRFQ}
            itemQuantities={itemQuantities}
            handleQuantityChange={handleQuantityChange}
            onSubmit={handleRFQSubmit}
            onCancel={handleRFQCancel}
            isSubmitDisabled={isSubmitDisabled}
            isSubmitting={isSubmitting}
            refetchVendors={refetchVendors}
            stockCriticalityFilter={daysFilter}
            CompanyAddress={getProjectById(projectId)?.company || ''}
            projectId={projectId}
            preferredWarehouseId={warehouseId}
          />
        </Modal>
      )}

      {OpenAddMaterial && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Add Materials"
          onClose={() => setOpenAddMaterial(false)}>
          <AddMaterials
            onSubmit={() => {
              toast.success('Material created successfully');
              fetchInventory();
            }}
            onClose={() => setOpenAddMaterial(false)}
            onSuccess={() => fetchInventory()}
            hideBrand={true}
            projectId={projectId}
          />
        </Modal>
      )}

      {/* Switch off anyway: only offered when the sole block is a stale requirement */}
      {forceConfirm && (
        <Modal
          isOpen={true}
          width={35}
          name="Switch off this item?"
          onClose={() => setForceConfirm(null)}>
          <p className="text-sm text-gray-700">{forceConfirm.message}</p>
          <p className="text-sm text-gray-500 mt-2">
            The requirement stays as it is. The item will be hidden from pickers and left out of
            stock health until it is switched back on.
          </p>
          <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
            <button
              type="button"
              className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700"
              onClick={() => setForceConfirm(null)}>
              Keep it on
            </button>
            <button
              type="button"
              disabled={pendingStatusIds.includes(forceConfirm.item.id)}
              className="px-4 py-2 text-sm rounded-md bg-[#2a565e] text-white font-semibold"
              onClick={() => handleToggleStatus(forceConfirm.item, true)}>
              Switch off anyway
            </button>
          </div>
        </Modal>
      )}

      {/* Excel upload: ask why stock is changing before picking the file */}
      {uploadReason !== null && (
        <Modal
          isOpen={true}
          width={35}
          name="Upload stock sheet"
          onClose={() => setUploadReason(null)}>
          <label className="block text-sm">
            <span className="mb-1 block text-gray-600">Why are you changing stock?</span>
            <input
              type="text"
              autoFocus
              maxLength={255}
              value={uploadReason}
              onChange={(e) => setUploadReason(e.target.value)}
              placeholder="e.g. Opening stock, monthly count, correction"
              className="w-full p-2 border-[1px] border-stone-300 rounded"
            />
          </label>
          <p className="text-xs text-gray-500 mt-2">
            Saved with every stock change in this sheet, so it shows up in each item&apos;s stock
            history.
          </p>
          <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
            <button
              type="button"
              className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700"
              onClick={() => setUploadReason(null)}>
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm rounded-md bg-[#2a565e] text-white font-semibold disabled:opacity-60"
              onClick={() => {
                const reason = uploadReason;
                setUploadReason(null);
                handleUploadTemplate(reason);
              }}>
              Choose file
            </button>
          </div>
        </Modal>
      )}

      {historyItem && (
        <StockHistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />
      )}

      {/* Edit Inventory Modal */}
      {stockEntriesItem && (
        <StockEntriesModal
          item={stockEntriesItem}
          onClose={() => setStockEntriesItem(null)}
          onChanged={fetchInventory}
        />
      )}
      {isEditModalOpen && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          isOpen={isEditModalOpen}
          width={50}
          name="Edit Inventory"
          onClose={handleEditModalClose}>
          <form onSubmit={handleEditSubmit(onEditSubmit)}>
            {/* Item Information Display */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Item:</span>
                <span className="text-sm text-gray-900 font-semibold">
                  {editItem?.item_name || '---'}
                  {editItem?.unit && ` (${editItem.unit})`}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 justify-start items-center">
              <FormInput
                type="number"
                label="Avg Acquiring Days"
                register={editRegister}
                register_key="avg_acquiring_days"
                isRequired={true}
                errors={editErrors.avg_acquiring_days}
                minLength={1}
                width={31}
              />
              <FormInput
                type="number"
                label="Safety Net (%)"
                register={editRegister}
                register_key="safetyNet"
                isRequired={true}
                errors={editErrors.safetyNet}
                width={31}
                minLength={1}
                maxLength={3}
              />

              <FormInput
                type="number"
                label="Minimum Quantity"
                register={editRegister}
                register_key="minimumQuantity"
                isRequired={true}
                errors={editErrors.minimumQuantity}
                width={31}
                minLength={1}
                maxDecimals={3}
              />
              <FormInput
                type="number"
                label="Physical Stock"
                register={editRegister}
                register_key="physicalStock"
                isRequired={true}
                errors={editErrors.physicalStock}
                width={31}
                minLength={1}
                maxDecimals={3}
                onType={setEditPhysicalStock}
              />
              <FormInput
                label="Reason"
                register={editRegister}
                register_key="reason"
                isRequired={physicalStockChanged}
                errors={editErrors.reason}
                width={100}
                maxLength={255}
                onType={setEditReason}
                placeholder={
                  physicalStockChanged
                    ? 'Why are you changing Physical Stock?'
                    : 'Needed only if Physical Stock changes'
                }
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
              <Button
                customStyle={{
                  height: '2.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none'
                }}
                type="button"
                onClick={handleEditModalClose}>
                Cancel
              </Button>
              <Button
                customStyle={{
                  height: '2.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: 'none'
                }}
                type="submit"
                disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default Stock;
